import React, { PureComponent, Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import './stylesheet.css';

import _ from 'lodash';
import classNames from 'classnames';

import * as baseActions from '../../../../actions/base';

import {
  addVerbunden,
  declaredKennungen,
  DOD,
  isValidWirkung,
  isZiel,
  KONTRAKT,
  KONTRAKT_ID,
  KONTRAKT_MIT,
  KONTRAKT_START,
  KONTRAKT_VERBUNDEN,
  KONTRAKT_ZUSAGE,
  kennung,
  parseVerbunden,
  propertyValue,
  resolveKontrakt,
  setProperty,
  wirkungProperty,
  WIRKUNG_DIMENSIONS,
  ZIEL_TAG,
} from '../../../../lib/kontrakt';
import { renderAsText } from '../../../../lib/timestamps';

// The labels are German on purpose. `Zusage`, `Gegenüber` and `Wirkung` are
// glossary terms of the format this popup writes (CONTEXT.md of the
// entropie-dashboard); an English label would name a different concept than
// the property underneath it. Code and comments stay English, as everywhere
// else in organice.
const ORIGIN_LABELS = {
  declared: 'hier erklärt',
  explicit: 'ausdrücklich zugeordnet',
  inherited: 'vom Teilbaum geerbt',
};

const WIRKUNG_LABELS = {
  ZIEL: 'Ziel',
  ERGEBNIS: 'Ergebnis',
  FINANZ: 'Finanz',
  AUSSEN: 'Aussen',
};

const DEFAULT_GEGENUEBER = ['selbst'];

class KontraktEditorModal extends PureComponent {
  constructor(props) {
    super(props);

    _.bindAll(this, [
      'handleDeclare',
      'handleVerbundenDraftChange',
      'handleAddVerbunden',
      'handleZielToggle',
      'handleDeadlineClick',
    ]);

    this.state = { verbundenDraft: '' };
  }

  setProp(property) {
    return (value) =>
      this.props.onPropertyListItemsChange(
        setProperty(this.props.header.get('propertyListItems'), property, value)
      );
  }

  handleFieldChange(property, normalise = null) {
    return (event) => {
      const value = event.target.value;
      this.setProp(property)(normalise ? normalise(value) : value);
    };
  }

  // Mirrors `entropie-kontrakt-erklaeren`: the Kennung is proposed from the
  // heading rather than invented, because it is a promise to the future -
  // renaming one rewrites membership retroactively in every file and every
  // export that already carries it.
  handleDeclare() {
    const { header } = this.props;
    const proposal = kennung(header.getIn(['titleLine', 'rawTitle']));
    this.setProp(KONTRAKT_ID)(proposal || 'kontrakt');
  }

  handleVerbundenDraftChange(event) {
    this.setState({ verbundenDraft: event.target.value });
  }

  handleAddVerbunden() {
    const { header } = this.props;
    const next = addVerbunden(propertyValue(header, KONTRAKT_VERBUNDEN), this.state.verbundenDraft);
    this.setProp(KONTRAKT_VERBUNDEN)(next);
    this.setState({ verbundenDraft: '' });
  }

  handleRemoveVerbunden(target) {
    return () => {
      const remaining = parseVerbunden(propertyValue(this.props.header, KONTRAKT_VERBUNDEN)).filter(
        (entry) => entry !== target
      );
      this.setProp(KONTRAKT_VERBUNDEN)(remaining.join(' '));
    };
  }

  handleWirkungClick(dimension, value) {
    // Clicking the step that is already set clears it again, so a value can go
    // back to being derived - the view distinguishes `gesetzt` from measured,
    // and a number nobody meant to set is worse than no number.
    return () => {
      const property = wirkungProperty(dimension);
      const current = propertyValue(this.props.header, property);
      this.setProp(property)(current === value ? '' : value);
    };
  }

  handleZielToggle() {
    const { header, onTagsChange } = this.props;
    const tags = header.getIn(['titleLine', 'tags']);

    onTagsChange(
      isZiel(header)
        ? tags.filter((tag) => `${tag}`.toLowerCase() !== ZIEL_TAG)
        : tags.push(ZIEL_TAG)
    );
  }

  // A Ziel needs a Zieltermin, and organice already has a timestamp editor for
  // exactly that - switching to it beats rebuilding a date picker here, and is
  // what `entropie-ziel-setzen` does when it calls `org-deadline`.
  handleDeadlineClick() {
    const { header, onSwitch, base } = this.props;
    const planningItemIndex = header
      .get('planningItems')
      .findIndex((item) => item.get('type') === 'DEADLINE');

    if (onSwitch) onSwitch();
    base.activatePopup('deadline-editor', {
      headerId: header.get('id'),
      planningItemIndex,
    });
  }

  renderStrand(strand) {
    if (!strand.kennung) {
      return (
        <div className="kontrakt-editor__strand kontrakt-editor__strand--none">
          Kein Strang — dieser Knoten fällt in den Sammelstrang seines Systems.
        </div>
      );
    }

    return (
      <div className="kontrakt-editor__strand">
        <span className="kontrakt-editor__strand-kennung">{strand.kennung}</span>
        <span className="kontrakt-editor__strand-origin">{ORIGIN_LABELS[strand.origin]}</span>
      </div>
    );
  }

  renderField(label, property, { placeholder = '', list = null, hint = null, normalise = null }) {
    const value = propertyValue(this.props.header, property) || '';

    return (
      <div className="kontrakt-editor__field">
        <label className="kontrakt-editor__label">{label}</label>
        <input
          type="text"
          className="textfield kontrakt-editor__textfield"
          value={value}
          placeholder={placeholder}
          list={list}
          onChange={this.handleFieldChange(property, normalise)}
          data-testid={`kontrakt-editor-${property}`}
        />
        {hint && <div className="kontrakt-editor__hint">{hint}</div>}
      </div>
    );
  }

  renderDeclaration(allKennungen) {
    const { header } = this.props;
    const verbunden = parseVerbunden(propertyValue(header, KONTRAKT_VERBUNDEN));

    return (
      <Fragment>
        {this.renderField('Kennung', KONTRAKT_ID, {
          placeholder: 'kennung-in-kleinbuchstaben',
          normalise: kennung,
          hint:
            'Steht in jeder exportierten Datei. Wird sie umbenannt, ändert sich die Zugehörigkeit rückwirkend.',
        })}
        {this.renderField('Gegenüber', KONTRAKT_MIT, {
          placeholder: 'selbst',
          list: 'kontrakt-editor__datalist-gegenueber',
        })}
        {this.renderField('Zusage', KONTRAKT_ZUSAGE, {
          placeholder: 'Was ist erfüllt, wenn es erfüllt ist',
        })}
        {this.renderField('Beginn', KONTRAKT_START, {
          placeholder: '<2026-04-01 Mi>',
          hint: 'Leer: der früheste Termin der Mitglieder.',
        })}

        <div className="kontrakt-editor__field">
          <label className="kontrakt-editor__label">Verbunden</label>
          {verbunden.length > 0 && (
            <div className="kontrakt-editor__chips">
              {verbunden.map((entry) => (
                <span className="kontrakt-editor__chip" key={entry}>
                  {entry}
                  <i
                    className="fas fa-times kontrakt-editor__chip-remove"
                    onClick={this.handleRemoveVerbunden(entry)}
                  />
                </span>
              ))}
            </div>
          )}
          <div className="kontrakt-editor__add-row">
            <input
              type="text"
              className="textfield kontrakt-editor__textfield"
              value={this.state.verbundenDraft}
              placeholder="andere Kennung"
              list="kontrakt-editor__datalist-kennungen"
              onChange={this.handleVerbundenDraftChange}
              data-testid="kontrakt-editor-verbunden-draft"
            />
            <button
              className="fas fa-plus btn btn--circle kontrakt-editor__add-button"
              onClick={this.handleAddVerbunden}
              data-testid="kontrakt-editor-verbunden-add"
            />
          </div>
          <div className="kontrakt-editor__hint">
            Verbindungen sind gegenseitig — die Gegenrichtung braucht keinen Eintrag.
          </div>
        </div>

        <div className="kontrakt-editor__field">
          <label className="kontrakt-editor__label">Wirkung</label>
          {WIRKUNG_DIMENSIONS.map((dimension) => {
            const current = propertyValue(this.props.header, wirkungProperty(dimension));

            return (
              <div className="kontrakt-editor__wirkung-row" key={dimension}>
                <span className="kontrakt-editor__wirkung-name">{WIRKUNG_LABELS[dimension]}</span>
                {['0', '1', '2', '3'].map((value) => (
                  <button
                    key={value}
                    className={classNames('btn kontrakt-editor__wirkung-step', {
                      'kontrakt-editor__wirkung-step--set': current === value,
                    })}
                    onClick={this.handleWirkungClick(dimension, value)}
                    data-testid={`kontrakt-editor-wirkung-${dimension}-${value}`}
                  >
                    {value}
                  </button>
                ))}
                <span className="kontrakt-editor__wirkung-state">
                  {isValidWirkung(current) ? 'gesetzt' : 'abgeleitet'}
                </span>
              </div>
            );
          })}
          <div className="kontrakt-editor__hint">
            Ein gesetzter Wert ersetzt den abgeleiteten vollständig. Nochmal tippen macht ihn wieder
            abgeleitet.
          </div>
        </div>

        <datalist id="kontrakt-editor__datalist-kennungen">
          {allKennungen.map((entry) => (
            <option key={entry} value={entry} />
          ))}
        </datalist>
      </Fragment>
    );
  }

  renderAssignment(allKennungen) {
    return (
      <Fragment>
        {this.renderField('Zuordnen', KONTRAKT, {
          placeholder: 'Kennung eines Strangs',
          list: 'kontrakt-editor__datalist-kennungen',
          hint:
            'Schlägt die Vererbung — für Punkte, die woanders stehen als ihr Strang. Eine Kennung, die niemand erklärt, bleibt sichtbar statt zu verschwinden.',
        })}

        <div className="kontrakt-editor__field">
          <button
            className="btn btn--secondary kontrakt-editor__declare"
            onClick={this.handleDeclare}
            data-testid="kontrakt-editor-declare"
          >
            Kontrakt hier erklären
          </button>
          <div className="kontrakt-editor__hint">
            Erklärt einen Strang an dieser Überschrift. Alles darunter gehört dann dazu.
          </div>
        </div>

        <datalist id="kontrakt-editor__datalist-kennungen">
          {allKennungen.map((entry) => (
            <option key={entry} value={entry} />
          ))}
        </datalist>
      </Fragment>
    );
  }

  renderZiel() {
    const { header } = this.props;
    const ziel = isZiel(header);
    const deadline = header.get('planningItems').find((item) => item.get('type') === 'DEADLINE');

    return (
      <div className="kontrakt-editor__section">
        <div className="kontrakt-editor__section-title">Ziel</div>

        <div className="kontrakt-editor__field">
          <button
            className={classNames('btn kontrakt-editor__ziel-toggle', {
              'kontrakt-editor__ziel-toggle--on': ziel,
            })}
            onClick={this.handleZielToggle}
            data-testid="kontrakt-editor-ziel-toggle"
          >
            <i className={ziel ? 'fas fa-check-square' : 'far fa-square'} />
            {' :ziel:'}
          </button>
          <div className="kontrakt-editor__hint">
            Der sichtbare Endpunkt des Strangs. Das Tag erbt nicht — ein Ziel ist immer genau eine
            Überschrift.
          </div>
        </div>

        {ziel && (
          <Fragment>
            <div className="kontrakt-editor__field">
              <label className="kontrakt-editor__label">Zieltermin</label>
              <button
                className="btn btn--secondary kontrakt-editor__deadline"
                onClick={this.handleDeadlineClick}
                data-testid="kontrakt-editor-deadline"
              >
                {deadline ? renderAsText(deadline.get('timestamp')) : 'Zieltermin setzen'}
              </button>
            </div>

            {this.renderField('Definition-of-Done', DOD, {
              placeholder: 'Woran erkennt man, dass es erreicht ist',
              hint: 'Kein Wunsch, sondern ein Nachweis: unterschrieben, ausgestellt, geprüft.',
            })}
          </Fragment>
        )}
      </div>
    );
  }

  render() {
    const { header, headers } = this.props;
    if (!header) return null;

    const strand = resolveKontrakt(headers, header.get('id'));
    const declares = !!propertyValue(header, KONTRAKT_ID);
    const allKennungen = declaredKennungen(headers);
    const gegenueber = _.uniq([
      ...DEFAULT_GEGENUEBER,
      ...headers
        .map((candidate) => propertyValue(candidate, KONTRAKT_MIT))
        .filter((value) => !!value)
        .toJS(),
    ]);

    return (
      <>
        <h2 className="drawer-modal__title" data-testid="kontrakt-editor-title">
          Kontrakt
        </h2>

        {this.renderStrand(strand)}

        <datalist id="kontrakt-editor__datalist-gegenueber">
          {gegenueber.map((entry) => (
            <option key={entry} value={entry} />
          ))}
        </datalist>

        <div className="kontrakt-editor__section">
          <div className="kontrakt-editor__section-title">
            {declares ? 'Erklärung' : 'Zugehörigkeit'}
          </div>
          {declares ? this.renderDeclaration(allKennungen) : this.renderAssignment(allKennungen)}
        </div>

        {this.renderZiel()}
      </>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  base: bindActionCreators(baseActions, dispatch),
});

export default connect(null, mapDispatchToProps)(KontraktEditorModal);
